// enumeratePanels.js — skaido VIENO baldo vieneto (furniture group) kabinetus
// į PLOKŠČIŲ, ATSKIRŲ skydelių sąrašą gamybiniam brėžiniui: kiekviena šoninė
// sienelė, viršus, apačia, kiekviena lentyna, kiekviena vidinė pertvara,
// kiekvienos durelės/stalčiaus fasadas — SAVO objektu, su realiais pjovimo
// matmenimis IR furnitūros gręžimo skylių pozicijomis (lentynų kaiščiai,
// durų vyrių tašelės + tvirtinimo plokštelės, stalčių bėgelių varžtai).
//
// Sąmoningai NEIMPORTUOJA calculatePrice.js geometrijos — ten naudojama
// innerHeight formulė NEATIMA pagrindo (basisHeight) aukščio, o Cabinet2D
// (View2D.jsx, realiai naudojamas ir vizualiai patikrintas kasdien) TAI daro
// (innerBottomY = visualBaseHeight + wallThickness). Šis modulis seka
// TIKSLIAI Cabinet2D geometrijos susitarimą (ne calculatePrice.js), nes
// brėžinio TIKSLUMAS yra visos šios funkcijos paskirtis — nematoma pjovimo
// klaida čia yra daug pavojingesnė nei nematoma smulki kainos paklaida.
import {
    HINGE_END_MARGIN, hingeCountForHeight, DRAWER_SLIDE_HOLE_Y, HINGE_PLATE_HOLE_OFFSET,
} from './hardwareStandards';

const SEAM_EPSILON = 0.006; // 6mm tolerancija sujungiant durų/stalčiaus kraštą su fizine sienele

// Sekcijos ribų skaičiavimas iš cabinet.structure medžio — ta pati logika,
// kaip View2D.jsx/calculatePrice.js/SingleCabinet3D.jsx (žinomas, sąmoningai
// nešalinamas trigubinimas šiame projekte — žr. AllInnerPanelDividers).
const getSectionBounds = (structure, path, innerWidth, innerHeight, wallThickness) => {
    const parts = String(path ?? '').split('-').slice(1);
    let node = structure || {};
    let x = 0, y = 0, w = innerWidth, h = innerHeight;
    for (const part of parts) {
        const i = parseInt(part, 10);
        if (!node.split || !node.children) break;
        const isH = node.split === 'h';
        const ratio = node.ratio ?? 0.5;
        const available = isH ? h - wallThickness : w - wallThickness;
        const child0Size = available * ratio;
        const child1Size = available * (1 - ratio);
        if (isH) {
            if (i === 0) { y += child1Size + wallThickness; h = child0Size; }
            else { h = child1Size; }
        } else {
            if (i === 1) { x += child0Size + wallThickness; w = child1Size; }
            else { w = child0Size; }
        }
        node = node.children[i];
    }
    return { x, y, width: w, height: h };
};

// Visų vidinių daliklių (lentynų IR vertikalių pertvarų) surinkimas per VISĄ
// medžio gylį — palaiko ir dvejetainį (ratio) IR N-inį (sizes) skaidymą (žr.
// SingleCabinet3D.jsx AllInnerPanelDividers.collect — ta pati logika, tik
// grąžinama kaip duomenys, ne piešiama).
const collectDividers = (node, wallThickness, x, y, w, h, out) => {
    if (!node || !node.split || !(node.children || []).length) return;
    const isH = node.split === 'h';
    const N = node.children.length;
    if (N < 2) return;

    if (N === 2) {
        const ratio = node.ratio ?? 0.5;
        const available = isH ? (h - wallThickness) : (w - wallThickness);
        const c0 = available * ratio;
        const c1 = available * (1 - ratio);
        if (isH) {
            out.push({ isH: true, cx: x + w / 2, cy: y + c1 + wallThickness / 2, dw: w });
            collectDividers(node.children[0], wallThickness, x, y + c1 + wallThickness, w, c0, out);
            collectDividers(node.children[1], wallThickness, x, y, w, c1, out);
        } else {
            out.push({ isH: false, cx: x + c0 + wallThickness / 2, cy: y + h / 2, dh: h });
            collectDividers(node.children[0], wallThickness, x, y, c0, h, out);
            collectDividers(node.children[1], wallThickness, x + c0 + wallThickness, y, c1, h, out);
        }
    } else {
        const available = isH ? (h - wallThickness * (N - 1)) : (w - wallThickness * (N - 1));
        const sizes = (node.sizes || Array(N).fill(1 / N)).map(s => s * available);
        if (isH) {
            const yOffsets = new Array(N);
            let off = 0;
            for (let i = N - 1; i >= 0; i--) { yOffsets[i] = off; off += sizes[i] + wallThickness; }
            for (let i = 0; i < N - 1; i++) {
                out.push({ isH: true, cx: x + w / 2, cy: y + yOffsets[i] - wallThickness / 2, dw: w });
            }
            for (let i = 0; i < N; i++) collectDividers(node.children[i], wallThickness, x, y + yOffsets[i], w, sizes[i], out);
        } else {
            let off = 0;
            for (let i = 0; i < N; i++) {
                if (i < N - 1) out.push({ isH: false, cx: x + off + sizes[i] + wallThickness / 2, cy: y + h / 2, dh: h });
                collectDividers(node.children[i], wallThickness, x + off, y, sizes[i], h, out);
                off += sizes[i] + wallThickness;
            }
        }
    }
};

// Sekcijos (horizontalūs lentynų tarpai) — TA PATI logika ir apribojimas,
// kaip Cabinet2D: sectionOptions (kabyklos/stalčiai/pantografai ir t.t.)
// priskiriami PER visą kabineto plotį iš `cabinet.shelves`, ne per
// individualius vertikalios pertvaros stulpelius — vidiniai elementai šioje
// programoje šiuo metu neturi vertikalios pertvaros supratimo, tad stalčių
// bėgelių skylės čia tvirtinamos prie IŠORINIŲ šoninių sienelių (ne prie
// vidinių pertvarų) — atitinka esamą, jau naudojamą supaprastinimą.
// `index` čia SĄMONINGAI atkartoja TIKSLIAI tą pačią schemą, kaip Cabinet2D
// (View2D.jsx) — indeksas = lentynos, kuri UŽDARO šią sekciją iš viršaus,
// eilės numeris rikiuotame masyve (arba sortedShelves.length paskutinei,
// niekuo neuždarytai sekcijai). `sectionOptions` raktai (`${fid}-${ci}-${idx}`)
// yra IŠSAUGOTI su ŠIA schema kitur programoje — bet koks nukrypimas (kitokia
// filtravimo riba ar atskiras sekvencinis skaitiklis) sutrikdytų sekcijos↔rakto
// atitikmenį visoms sekcijoms PO pirmo skirtumo.
const buildShelfSections = (cabinet, innerBottomY, innerTopY, wallThickness) => {
    const sortedShelves = (cabinet.shelves || [])
        .map(s => Number(s?.position?.[1]) || 0)
        .sort((a, b) => a - b);
    const sections = [];
    let curY = innerBottomY;
    sortedShelves.forEach((sy, idx) => {
        const sh = Math.max(0.001, sy - wallThickness / 2 - curY);
        if (sh > 0) sections.push({ index: idx, startY: curY, height: sh });
        curY = sy + wallThickness / 2;
    });
    const lastH = Math.max(0.001, innerTopY - curY);
    if (lastH > 0) sections.push({ index: sortedShelves.length, startY: curY, height: lastH });
    return sections;
};

let panelSeq = 0;
const nextId = (prefix) => `${prefix}-${panelSeq++}`;

// Trumpas sekvencinis KODAS kiekvienam skydeliui (rodomas ANT brėžinio VIETOJ
// pilno pavadinimo — žr. View2D.jsx ProductionPanel) — pilnas pavadinimas,
// matmenys, medžiaga ir gręžimo vieta lieka TIK lentelėje (SummaryView.jsx
// PanelsTable), į kurią kodas nurodo. Skaitiklis atstatomas kiekvienam
// enumeratePanels() iškvietimui, tad kodai visada prasideda nuo 1 kiekvienam
// baldo vienetui (atitinka lentelės, kuri TAIP PAT rodoma per vieną baldo
// vienetą, apimtį).
let codeSeq = 1;
const nextCode = () => String(codeSeq++);

// Medžiagos etiketė (LMDP/MDF + kodas) — ta pati numatytosios plokštės
// prielaida, kaip calculatePrice.js addPanel.
const materialLabel = (plate) => {
    const p = plate || { panelType: 0, code: 'STD' };
    return `${p.panelType === 1 ? 'MDF' : 'LMDP'} ${p.code || 'STD'}`;
};

// enumeratePanels(cabinets, settings) → flat panel list for ONE furniture
// group. `settings` needs: wallThickness (default 0.018), mergedSections,
// doorColors, sectionOptions, and (for the "Medžiaga" table column)
// selectedPlate/separatePlates/leftPlate/topPlate/bottomPlate/rightPlate/doorPlates —
// ta pati susitarimo schema, kaip calculatePrice.js addPanel.
export const enumeratePanels = (cabinets, settings = {}) => {
    panelSeq = 0;
    codeSeq = 1;
    const wallThickness = settings.wallThickness ?? 0.018;
    const mergedSections = settings.mergedSections || [];
    const doorColors = settings.doorColors || {};
    const sectionOptions = settings.sectionOptions || {};
    const doorPlates = settings.doorPlates || {};
    const {
        selectedPlate, separatePlates, leftPlate, topPlate, bottomPlate, rightPlate,
    } = settings;
    const carcassMaterial = (side) => {
        if (!separatePlates) return materialLabel(selectedPlate);
        const bySide = { left: leftPlate, top: topPlate, bottom: bottomPlate, right: rightPlate };
        return materialLabel(bySide[side] || selectedPlate);
    };
    const panels = [];
    const safeCabinets = Array.isArray(cabinets) ? cabinets : [];

    safeCabinets.forEach((cab, ci) => {
        const fid = cab.furnitureId;
        const depth = Number(cab.depth) || 0.6;
        const width = Number(cab.width) || 0;
        const height = Number(cab.height) || 0;
        const basisType = Number(cab.basisType) || 0;
        const basisHeight = Number(cab.basisHeight) || 0;
        // Senoji formulė ((basisType===4||basisType===5)?0:basisHeight) buvo seniai
        // nebesutampanti su View2D.jsx/SingleCabinet3D.jsx: basisType 4 ("Dešinė iki
        // grindų") klaidingai nulindavo pagrindo zoną (jis YRA vienas iš "turi
        // pagrindą" tipų, kaip 1/2/3/6), o basisType 5 ("Be pagrindo") naudojo 0 vietoj
        // dabartinio 0,015 (žr. ClickUp "2. Furnitūra. Lankstai. Skaičiavimas
        // nesilaiko taisyklės" — šis modulis TIESIOGIAI veikia vyrių kiekio
        // skaičiavimą per hingeCountForHeight žemiau, tad klaidingas aukštis čia
        // reiškia klaidingą vyrių skaičių gamybiniame brėžinyje). Dabar TA PATI
        // formulė, kaip View2D.jsx/SingleCabinet3D.jsx.
        const visualBaseHeight = basisType === 0 ? 0 : (basisType === 5 ? 0.015
          : ((basisType === 7 || basisType === 8) ? wallThickness : basisHeight));
        const carcassHeight = Math.max(0.001, height - visualBaseHeight);
        const innerBottomY = visualBaseHeight + wallThickness;
        const innerTopY = height - wallThickness;
        const innerUsableH = Math.max(0.001, innerTopY - innerBottomY);
        const innerWidth = Math.max(0.001, width - wallThickness * 2);
        const label = `Spinta ${ci + 1}`;

        // ── 4 pagrindiniai karkaso skydeliai — KIEKVIENAS kabinetas turi
        // savo NEPRIKLAUSOMAS šonines sieneles (ta pati prielaida, kaip
        // calculatePrice.js addPanel — ten NĖRA `index===0` sąlygos, nes
        // kainodara traktuoja kiekvieną kabinetą kaip pilnai atskirą dėžę,
        // net jei brėžinyje gretimos sienelės vizualiai nedubliuojamos).
        const sideLeft = {
            id: nextId('side-l'), code: nextCode(), cabinetIndex: ci, kind: 'side-left',
            label: `${label} — Šoninė kairė`, width: depth, height: carcassHeight,
            material: carcassMaterial('left'),
            holes: { shelfPinColumns: true, hingePlates: [], drawerSlides: [] },
        };
        const sideRight = {
            id: nextId('side-r'), code: nextCode(), cabinetIndex: ci, kind: 'side-right',
            label: `${label} — Šoninė dešinė`, width: depth, height: carcassHeight,
            material: carcassMaterial('right'),
            holes: { shelfPinColumns: true, hingePlates: [], drawerSlides: [] },
        };
        panels.push(sideLeft, sideRight);
        panels.push({
            id: nextId('top'), code: nextCode(), cabinetIndex: ci, kind: 'top',
            label: `${label} — Viršus`, width: innerWidth, height: depth,
            material: carcassMaterial('top'), holes: {},
        });
        if (basisType !== 0) {
            panels.push({
                id: nextId('bottom'), code: nextCode(), cabinetIndex: ci, kind: 'bottom',
                label: `${label} — Apačia`, width: innerWidth, height: depth,
                material: carcassMaterial('bottom'), holes: {},
            });
        }

        // ── Vidiniai dalikliai (lentynos + vertikalios pertvaros) ──
        const dividers = [];
        if (cab.structure?.split) {
            collectDividers(cab.structure, wallThickness, 0, 0, innerWidth, innerUsableH, dividers);
        }

        dividers.forEach((d) => {
            if (d.isH) {
                panels.push({
                    id: nextId('shelf'), code: nextCode(), cabinetIndex: ci, kind: 'shelf',
                    label: `${label} — Lentyna`, width: d.dw, height: depth,
                    material: materialLabel(selectedPlate), holes: {},
                });
            } else {
                panels.push({
                    id: nextId('partition'), code: nextCode(), cabinetIndex: ci, kind: 'partition-v',
                    label: `${label} — Vidinė pertvara`, width: depth, height: d.dh,
                    material: materialLabel(selectedPlate),
                    holes: { shelfPinColumns: true, hingePlates: [], drawerSlides: [] },
                    _seamX: d.cx,
                });
            }
        });

        // Vertikalios "siūlės" (kur gali remtis durų vyris arba stalčiaus
        // bėgelis) — išorinės sienelės + kiekviena vidinė pertvara.
        const seams = [
            { x: wallThickness, kind: 'side-left', panel: sideLeft },
            { x: width - wallThickness, kind: 'side-right', panel: sideRight },
            ...panels.filter(p => p.cabinetIndex === ci && p.kind === 'partition-v')
                .map(p => ({ x: p._seamX + wallThickness, kind: 'partition-v', panel: p })),
        ];
        const nearestSeam = (localX) => {
            let best = null, bestDist = Infinity;
            seams.forEach(s => {
                const d0 = Math.abs(s.x - localX);
                if (d0 < bestDist) { bestDist = d0; best = s; }
            });
            return bestDist <= SEAM_EPSILON ? best : null;
        };

        // ── Stalčių bėgelių varžtai — sekcijos (lentynų tarpai) su
        // sectionOptions tipu 4/5/6/7 (stalčius/dvigubas), tvirtinamos prie
        // ABIEJŲ išorinių šoninių sienelių (žr. modulio antraštės pastabą
        // dėl vertikalios pertvaros apribojimo).
        const sections = buildShelfSections(cab, innerBottomY, innerTopY, wallThickness);
        sections.forEach((sec) => {
            const optKey = `${fid}-${ci}-${sec.index}`;
            const optIdx = sectionOptions[optKey] || 0;
            if (![4, 5, 6, 7].includes(optIdx)) return;
            const localY = (sec.startY - visualBaseHeight) + DRAWER_SLIDE_HOLE_Y;
            sideLeft.holes.drawerSlides.push({ y: localY });
            sideRight.holes.drawerSlides.push({ y: localY });
        });

        // ── Durelės / fasadai (vyrių tašelės + tvirtinimo plokštelės) ──
        const cabMerged = mergedSections.filter(s => s.furnitureId === fid && s.cabinetIndex === ci);
        const groupIds = [...new Set(cabMerged.map(s => s.groupId))];
        const doorRects = [];
        groupIds.forEach((gid) => {
            const entries = cabMerged.filter(s => s.groupId === gid);
            const paths = new Set();
            entries.forEach(s => { paths.add(s.sectionIndex); paths.add(s.targetSectionIndex); });
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            paths.forEach(p => {
                if (p === undefined || p === null) return;
                const b = getSectionBounds(cab.structure, p, innerWidth, innerUsableH, wallThickness);
                minX = Math.min(minX, b.x); minY = Math.min(minY, b.y);
                maxX = Math.max(maxX, b.x + b.width); maxY = Math.max(maxY, b.y + b.height);
            });
            if (!isFinite(minX)) return;
            const primaryPath = entries[0].sectionIndex;
            const doorType = doorColors[`${fid}-${ci}-${primaryPath}`] || 0;
            if (!doorType) return;
            doorRects.push({
                x: wallThickness + minX, y: innerBottomY + minY,
                w: maxX - minX, h: maxY - minY, doorType, key: `door-${gid}`, groupId: gid,
            });
        });
        const mergedPathSet = new Set([...cabMerged.map(s => s.sectionIndex), ...cabMerged.map(s => s.targetSectionIndex)]);
        Object.entries(doorColors).forEach(([key, dt]) => {
            if (!dt) return;
            const prefix = `${fid}-${ci}-`;
            if (!key.startsWith(prefix)) return;
            const path = key.slice(prefix.length);
            if (mergedPathSet.has(path)) return;
            const b = getSectionBounds(cab.structure, path, innerWidth, innerUsableH, wallThickness);
            if (!b) return;
            doorRects.push({
                x: wallThickness + b.x, y: innerBottomY + b.y,
                w: b.width, h: b.height, doorType: dt, key: `door-solo-${key}`, groupId: null,
            });
        });

        // Vieno lapo durelės (leaf) — pridedamas ATSKIRAS 'door' skydelis su
        // vyrių tašelėmis TIK ties SAVO vyrio kraštu, plius atitinkamos
        // tvirtinimo plokštelės skylės ant fizinio šoninio skydelio/pertvaros.
        const addLeaf = (leafW, h, y, hingeAbsX, hingeSign, doorMaterial) => {
            const n = hingeCountForHeight(h);
            const margin = Math.min(HINGE_END_MARGIN, h / (2 * (n - 1 || 1)));
            const step = n > 1 ? (h - margin * 2) / (n - 1) : 0;
            const hingeYsAbs = Array.from({ length: n }, (_, i) => y + margin + i * step);

            panels.push({
                id: nextId('door'), code: nextCode(), cabinetIndex: ci, kind: 'door',
                label: `${label} — Durelės`, width: leafW, height: h, material: doorMaterial,
                holes: { hingeCups: [{ side: hingeSign === 1 ? 'left' : 'right', hDoor: h }] },
            });

            const seam = nearestSeam(hingeAbsX);
            if (!seam) return;
            hingeYsAbs.forEach((yAbs) => {
                seam.panel.holes.hingePlates.push({ y: yAbs - visualBaseHeight, offset: HINGE_PLATE_HOLE_OFFSET });
            });
        };

        doorRects.forEach((dr) => {
            if (dr.doorType === 6) return; // "stalčius" vaizdinis kodas — ne durelės, be vyrių
            if (dr.doorType === 3) {
                // Dvigubos durelės — DVI FIZIŠKAI ATSKIROS durelių lapai (ne
                // vienas skydelis su vyriais iš abiejų pusių), kiekvienas per
                // pusę pločio, vyriai — kiekvieno lapo SAVO IŠORINIAME krašte
                // (atitinka DoorArcSymbol vaizdinį susitarimą View2D.jsx:
                // kairysis lapas sukasi nuo kairės, dešinysis — nuo dešinės).
                // Kiekvienas lapas GALI turėti savo plokštę (žr. ClickUp "3.0
                // Išorė. Meniu - Dviguboms durims leidžiame pasirinkti
                // konkrečias plokštes... T.y. kairinėms ir dešininėms skirtingi
                // pasirinkimai") — PlatesFillingsAndHandles.jsx rašo
                // doorPlates[`${groupId}::L`]/`::R`; jei dar nepasirinkta
                // (pvz. sena, prieš šį pakeitimą sukurta durų grupė), krentame
                // į neprifiksuotą dr.groupId raktą, tada į selectedPlate.
                const half = dr.w / 2;
                const materialL = materialLabel(dr.groupId ? (doorPlates[`${dr.groupId}::L`] || doorPlates[dr.groupId]) : selectedPlate);
                const materialR = materialLabel(dr.groupId ? (doorPlates[`${dr.groupId}::R`] || doorPlates[dr.groupId]) : selectedPlate);
                addLeaf(half, dr.h, dr.y, dr.x, 1, materialL);
                addLeaf(half, dr.h, dr.y, dr.x + dr.w, -1, materialR);
                return;
            }
            const doorMaterial = materialLabel(dr.groupId ? doorPlates[dr.groupId] : selectedPlate);
            if (dr.doorType === 1) {
                addLeaf(dr.w, dr.h, dr.y, dr.x + dr.w, -1, doorMaterial);
            } else if (dr.doorType === 2) {
                addLeaf(dr.w, dr.h, dr.y, dr.x, 1, doorMaterial);
            }
            // 4/5 — atverčiamos viršus/apačia, be šoninių vyrių šiame modelyje
        });
    });

    return panels;
};

export default enumeratePanels;
